import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Check, ChevronDown } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";

/**
 * Reusable premium enterprise product landing page.
 *
 * Sections:  Hero → Vision → Why it matters → Core Capabilities → How it works →
 *            AI Intelligence → Mock dashboard → Benefits → FAQ → Request Demo
 *
 * Props:
 *   kicker            : "Project Intelligence Platform"
 *   name              : "SYNQ Projects"
 *   headline          : "Predict project health before it..."
 *   subhead           : "..."
 *   accentClass       : tailwind accent color for hero glow
 *   vision            : {title, body}
 *   whyMatters        : [{title, body}, ...]  (3-4 items)
 *   capabilities      : [{title, body, icon?}, ...]  (6-8 items)
 *   howItWorks        : [{title, body}, ...]  (3-4 steps)
 *   aiIntelligence    : {title, body, bullets: []}
 *   mockDashboard     : ReactNode (visual)
 *   benefits          : [string, ...] (5-8)
 *   faq               : [{q, a}, ...]
 */
export default function ProductLandingTemplate(props) {
  const {
    kicker, name, headline, subhead, accentClass = "bg-blue-200 dark:bg-blue-900/50",
    vision, whyMatters = [], capabilities = [], howItWorks = [],
    aiIntelligence, mockDashboard, benefits = [], faq = [],
  } = props;

  const [openFAQ, setOpenFAQ] = useState(0);

  return (
    <MarketingLayout>
      {/* ─── HERO ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className={`absolute -top-32 right-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-40 ${accentClass}`} />
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 pb-16 md:pt-28 md:pb-20">
          <div className="inline-flex items-center gap-2 border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur px-3 py-1.5 rounded-full mb-6">
            <span className="font-ibm-plex text-xs text-slate-600 dark:text-slate-400 tracking-wide">{kicker}</span>
          </div>
          <h1 data-testid={`${name.toLowerCase().replace(/\s+/g, "-")}-hero-headline`} className="font-outfit text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.05] max-w-4xl">
            {name}
            <br />
            <span className="text-slate-500 dark:text-slate-500">{headline}</span>
          </h1>
          <p className="mt-6 max-w-2xl font-ibm-plex text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
            {subhead}
          </p>
          <div className="mt-8 flex gap-3">
            <Link to={`/contact?intent=demo&product=${encodeURIComponent(name)}`}
              className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors">
              Request Demo <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/auth"
              className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* ─── VISION ──────────────────────────────────────────────────────── */}
      {vision && (
        <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
            <div className="grid md:grid-cols-3 gap-10 items-start">
              <div>
                <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Vision</div>
                <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">{vision.title}</h2>
              </div>
              <div className="md:col-span-2">
                <p className="font-ibm-plex text-lg md:text-xl leading-relaxed text-slate-700 dark:text-slate-300 max-w-3xl">{vision.body}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── WHY IT MATTERS ─────────────────────────────────────────────── */}
      {whyMatters.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-800">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
            <div className="max-w-3xl mb-12">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Why It Matters</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                The cost of blind execution has never been higher.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {whyMatters.map((w, i) => (
                <div key={i} className="border-l-2 border-slate-900 dark:border-white pl-5 py-2">
                  <h3 className="font-outfit font-bold text-lg text-slate-900 dark:text-white">{w.title}</h3>
                  <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CORE CAPABILITIES ─────────────────────────────────────────── */}
      {capabilities.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Core Capabilities</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">Everything you need to sense, decide, and act.</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((c, i) => (
                <div key={i} data-testid={`capability-${i}`} className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-2xl p-6 hover:border-slate-900 dark:hover:border-white transition-colors">
                  {c.icon && (
                    <div className="w-10 h-10 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center mb-4">
                      {c.icon}
                    </div>
                  )}
                  <h3 className="font-outfit font-bold text-base text-slate-900 dark:text-white">{c.title}</h3>
                  <p className="font-ibm-plex text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── HOW IT WORKS ──────────────────────────────────────────────── */}
      {howItWorks.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-800">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
            <div className="max-w-3xl mb-12">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">How It Works</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">From signal to systemic action — in four moves.</h2>
            </div>
            <ol className="space-y-6">
              {howItWorks.map((s, i) => (
                <li key={i} className="grid grid-cols-[64px_1fr] gap-6 border-b border-slate-100 dark:border-slate-800 pb-6 last:border-b-0">
                  <div className="font-outfit text-5xl font-black text-slate-200 dark:text-slate-800 leading-none">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <h3 className="font-outfit font-bold text-lg text-slate-900 dark:text-white">{s.title}</h3>
                    <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-2 leading-relaxed max-w-3xl">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ─── AI INTELLIGENCE ───────────────────────────────────────────── */}
      {aiIntelligence && (
        <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">AI Intelligence</div>
                <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">{aiIntelligence.title}</h2>
                <p className="font-ibm-plex text-lg text-slate-600 dark:text-slate-400 mt-5 leading-relaxed">{aiIntelligence.body}</p>
                {aiIntelligence.bullets?.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {aiIntelligence.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                        <span className="font-ibm-plex text-slate-700 dark:text-slate-300">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                {/* Abstract AI panel */}
                <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 aspect-square">
                  <div aria-hidden className="absolute inset-0 opacity-[0.05] dark:opacity-[0.15]" style={{
                    backgroundImage: "radial-gradient(circle at 50% 50%, rgba(37,99,235,0.6) 0%, transparent 60%)",
                  }} />
                  <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="space-y-2">
                      <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold">AI Advisor</div>
                      <div className="font-outfit text-2xl font-black text-slate-900 dark:text-white leading-tight">
                        &ldquo;Team Energy has dropped 12% this sprint.&rdquo;
                      </div>
                      <div className="font-ibm-plex text-sm text-slate-500 dark:text-slate-400">
                        Suggested action: reduce sprint intensity by 20% next cycle.
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {["Sense", "Understand", "Act"].map((s, i) => (
                        <div key={i} className="border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mb-2" />
                          <div className="font-ibm-plex text-xs text-slate-700 dark:text-slate-300 font-semibold">{s}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── MOCK DASHBOARD ────────────────────────────────────────────── */}
      {mockDashboard && (
        <section className="border-t border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">See It In Action</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">The intelligence surface, at a glance.</h2>
            </div>
            <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl shadow-slate-900/10 dark:shadow-black/40">
              {mockDashboard}
            </div>
          </div>
        </section>
      )}

      {/* ─── BENEFITS ──────────────────────────────────────────────────── */}
      {benefits.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
            <div className="max-w-3xl mb-12">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Benefits</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">What changes when SYNQ is on.</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="font-ibm-plex text-[15px] text-slate-800 dark:text-slate-200 leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── FAQ ───────────────────────────────────────────────────────── */}
      {faq.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-800">
          <div className="max-w-3xl mx-auto px-5 md:px-8 py-20">
            <div className="mb-10">
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">FAQ</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">Frequently asked.</h2>
            </div>
            <div className="border-t border-slate-200 dark:border-slate-800">
              {faq.map((f, i) => (
                <div key={i} className="border-b border-slate-200 dark:border-slate-800">
                  <button
                    data-testid={`faq-${i}`}
                    onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                    className="w-full flex items-center justify-between text-left py-5"
                  >
                    <span className="font-outfit font-bold text-base md:text-lg text-slate-900 dark:text-white pr-4">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${openFAQ === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFAQ === i && (
                    <p className="font-ibm-plex text-slate-600 dark:text-slate-400 pb-5 leading-relaxed">{f.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA ───────────────────────────────────────────────────────── */}
      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-10 md:p-14">
            <div aria-hidden className={`absolute -right-20 -top-20 w-80 h-80 rounded-full blur-3xl opacity-40 ${accentClass}`} />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="font-outfit text-3xl md:text-4xl font-black leading-tight">Ready to see {name} in action?</h2>
                <p className="font-ibm-plex text-slate-300 dark:text-slate-600 mt-3">Book a 30-minute walkthrough with our team.</p>
              </div>
              <Link
                to={`/contact?intent=demo&product=${encodeURIComponent(name)}`}
                data-testid={`${name.toLowerCase().replace(/\s+/g, "-")}-cta-demo`}
                className="inline-flex items-center gap-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px]"
              >
                Request Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
